import React, { useEffect, useRef } from "react";
import { Drawing, Scene } from "../SpatialConfigurationMode/types";
import { ViewportBounds, usePanAndZoom } from "./hooks/usePanAndZoom";
import { OriginAndScale } from "./shapes/OriginAndScale";

const DRAG_THRESHOLD = 5;

// There are better ways to compute this
// but for now it should work fine
const DRAWING_TOOL_PIXELS_HEIGHT = 800;

// Focusing on a selected scene in the main tool always re-centers on it. It
// only zooms in as a fallback when the current view is more zoomed out than
// this real-world width (in mm) — i.e. so far out the scene wouldn't be
// clearly visible. Converted to svg coordinate units (via mmScaleFactor) before
// being handed to the hook. Kept wide so focusing usually just pans and
// preserves the user's zoom.
const FOCUS_MAX_VIEW_WIDTH_MM = 25000;

type DrawingToolProps = {
  nodes: React.ReactNode;
  onDrawingClick?: (x: number, y: number) => void;
  drawing: Drawing;
  showOriginAndScale?;
  enableDrag?: boolean;
  selectedScene?: Scene;
  enableMiniMap?: boolean;
  // Bounding box (in svg coordinates) of all scene markers. When all of them
  // already fit on screen, selecting a scene won't pan/zoom.
  scenesBounds?: ViewportBounds;
};

export const DrawingTool = (props: DrawingToolProps) => {
  const {
    nodes,
    onDrawingClick,
    drawing,
    showOriginAndScale,
    enableDrag,
    selectedScene,
    enableMiniMap,
    scenesBounds,
  } = props;

  // Use different dimensions based on whether this is a minimap or main spatial tool
  const svgWidth = enableMiniMap ? 448 : 4000;
  const svgHeight = enableMiniMap ? 760 : 4000;

  // Where the current press started, in viewport (client) coordinates. Used to
  // tell clicks from drags: offsetX and the hook's dragStartPos are relative to
  // the event *target* (a scene circle, the floorplan image, or the svg), so
  // comparing them mixes coordinate origins and misjudges the distance.
  // clientX/clientY share one origin regardless of what was clicked.
  const dragStartClientPosRef = useRef<{ x: number; y: number } | null>(null);

  const { containerRef, origin, scale, resetOrigin, focusOnPoint } =
    usePanAndZoom({
      scale:
        (DRAWING_TOOL_PIXELS_HEIGHT /
          (drawing.height * drawing.mmScaleFactor)) *
        2,
      origin:
        enableMiniMap &&
        selectedScene &&
        selectedScene.x !== undefined &&
        selectedScene.y !== undefined
          ? {
              x:
                selectedScene.x -
                svgWidth /
                  2 /
                  ((DRAWING_TOOL_PIXELS_HEIGHT /
                    (drawing.height * drawing.mmScaleFactor)) *
                    2),
              y:
                selectedScene.y -
                svgHeight /
                  2 /
                  ((DRAWING_TOOL_PIXELS_HEIGHT /
                    (drawing.height * drawing.mmScaleFactor)) *
                    2),
            }
          : {
              x: -(drawing.originX * drawing.mmScaleFactor),
              y: -(drawing.originY * drawing.mmScaleFactor),
            },
      enableLeftClickDrag: enableDrag,
    });

  // When a scene becomes selected (via the spaces tree or the floor plan),
  // bring it into view. The minimap just re-centers (preserving its zoom). The
  // main tool leaves the view untouched when all scenes already fit on screen;
  // otherwise it re-centers on the scene and zooms in only when the current
  // view is too far out for it to be clearly visible (never zooms further out).
  useEffect(() => {
    if (
      !selectedScene ||
      selectedScene.x === undefined ||
      selectedScene.y === undefined
    ) {
      return;
    }

    if (enableMiniMap) {
      resetOrigin({
        x: selectedScene.x - svgWidth / 2 / scale,
        y: selectedScene.y - svgHeight / 2 / scale,
      });
      return;
    }

    focusOnPoint(
      { x: selectedScene.x, y: selectedScene.y },
      {
        maxViewWidthSvgUnits: FOCUS_MAX_VIEW_WIDTH_MM * drawing.mmScaleFactor,
        contentBounds: scenesBounds,
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedScene?.id, // Only trigger on scene selection change
    enableMiniMap,
    resetOrigin,
    focusOnPoint,
  ]);

  const handleMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    dragStartClientPosRef.current = { x: e.clientX, y: e.clientY };
  };

  const handleDrawingClick = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!onDrawingClick) {
      return;
    }

    // Suppress the click that ends a pan: if the pointer travelled beyond the
    // threshold since mousedown, it was a drag. Measured in client coordinates
    // (see dragStartClientPosRef).
    if (enableDrag && dragStartClientPosRef.current) {
      const dragDistance = Math.sqrt(
        Math.pow(e.clientX - dragStartClientPosRef.current.x, 2) +
          Math.pow(e.clientY - dragStartClientPosRef.current.y, 2),
      );
      if (dragDistance > DRAG_THRESHOLD) {
        return;
      }
    }

    // For the minimap, let the browser do the screen→svg conversion.
    // getScreenCTM maps the svg's user space (the coordinate space the scene
    // circles live in, after the viewBox transform) to screen coordinates, so
    // its inverse turns the click straight into world coordinates. The manual
    // offsetX-based math below is wrong here twice over: offsetX/Y are
    // relative to the event target (the circle or floorplan image, not the
    // svg), and the minimap css stretches the svg to 100%×100% of a panel
    // whose aspect ratio differs from the viewBox's, so preserveAspectRatio
    // letterboxes the content — no rect-ratio arithmetic accounts for that.
    if (enableMiniMap) {
      const screenCTM = e.currentTarget.getScreenCTM();
      if (screenCTM) {
        const worldPoint = new DOMPoint(e.clientX, e.clientY).matrixTransform(
          screenCTM.inverse(),
        );
        onDrawingClick(worldPoint.x, worldPoint.y);
        return;
      }
    }

    const clickX = e.nativeEvent.offsetX;
    const clickY = e.nativeEvent.offsetY;

    onDrawingClick(clickX / scale + origin.x, clickY / scale + origin.y);
  };

  return (
    <svg
      viewBox={`${origin.x} ${origin.y} ${svgWidth / scale} ${
        svgHeight / scale
      }`}
      width={`${svgWidth}px`}
      height={`${svgHeight}px`}
      xmlns="http://www.w3.org/2000/svg"
      onMouseDown={handleMouseDown}
      onClick={handleDrawingClick}
      // prevent opening context menu on right click - auxclick would not work
      // see https://developer.mozilla.org/en-US/docs/Web/API/Element/auxclick_event#preventing_default_actions
      onContextMenu={(e) => e.preventDefault()}
      ref={containerRef}
    >
      <image
        href={drawing.url}
        x={-(drawing.originX * drawing.mmScaleFactor)}
        y={-(drawing.originY * drawing.mmScaleFactor)}
        height={drawing.height * drawing.mmScaleFactor}
      />
      {showOriginAndScale && <OriginAndScale />}
      {nodes}
    </svg>
  );
};
